import React from 'react';
import { Alert, AlertDescription } from '../ui/alert';
import { ShieldCheck, UserCheck, Smartphone, WifiOff, ShieldAlert, Info, Check, AlertCircle } from 'lucide-react';

export const FaceVerificationBadge = ({ hasFaceReference, faceVerified, faceMatchScore, isOnline, offlineFaceDescriptor }) => {
  if (!hasFaceReference) return null;

  return (
    <div className="mb-4">
      {faceVerified ? (
        <Alert className="border-emerald-200 bg-emerald-50">
          <ShieldCheck className="w-4 h-4 text-emerald-600" />
          <AlertDescription className="text-emerald-800">
            <span className="font-medium">Wajah terverifikasi!</span> Anda dapat melanjutkan absensi.
            {faceMatchScore && <span className="text-xs ml-1">({faceMatchScore.toFixed(0)}% cocok)</span>}
          </AlertDescription>
        </Alert>
      ) : (
        <Alert className={`${isOnline ? 'border-indigo-200 bg-indigo-50' : 'border-amber-200 bg-amber-50'}`}>
          <UserCheck className={`w-4 h-4 ${isOnline ? 'text-indigo-600' : 'text-amber-600'}`} />
          <AlertDescription className={`${isOnline ? 'text-indigo-800' : 'text-amber-800'}`}>
            <span className="font-medium">
              {isOnline ? 'Verifikasi wajah aktif.' : 'Verifikasi wajah offline aktif.'}
            </span>
            {' '}Wajah akan diverifikasi saat submit absensi.
            {!isOnline && offlineFaceDescriptor && (
              <span className="text-xs block mt-1">Data wajah tersimpan di perangkat</span>
            )}
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};

export const DesktopWarning = ({ isMobileDevice }) => {
  if (isMobileDevice) return null;
  return (
    <Alert className="mb-4 border-amber-200 bg-amber-50">
      <Smartphone className="w-4 h-4 text-amber-600" />
      <AlertDescription className="text-amber-800">
        <strong>Peringatan:</strong> GPS di desktop/laptop tidak akurat. Disarankan menggunakan HP/tablet dengan GPS.
      </AlertDescription>
    </Alert>
  );
};

export const OfflineWarning = ({ isOnline }) => {
  if (isOnline) return null;
  return (
    <Alert className="mb-4 border-amber-200 bg-amber-50">
      <WifiOff className="w-4 h-4 text-amber-600" />
      <AlertDescription className="text-amber-800">
        Anda offline. Absensi akan disimpan dan disinkronkan saat online.
      </AlertDescription>
    </Alert>
  );
};

export const FakeGpsAlert = ({ fakeGpsWarning, fakeGpsIndicators, fakeGpsScore }) => {
  if (!fakeGpsWarning) return null;
  return (
    <Alert className="mb-4 border-red-500 bg-red-50" data-testid="fake-gps-alert">
      <ShieldAlert className="w-5 h-5 text-red-600" />
      <AlertDescription className="text-red-800">
        <strong className="text-red-700">PERINGATAN: INDIKASI FAKE GPS TERDETEKSI!</strong>
        {fakeGpsIndicators.length > 0 && (
          <div className="mt-2 space-y-1">
            {fakeGpsIndicators.map((indicator, idx) => (
              <div key={idx} className={`flex items-start gap-2 text-sm ${
                indicator.severity === 'critical' ? 'text-red-700 font-semibold' :
                indicator.severity === 'high' ? 'text-red-600' :
                indicator.severity === 'medium' ? 'text-orange-600' : 'text-amber-600'
              }`}>
                <span className="mt-0.5">
                  {indicator.severity === 'critical' ? '\u{1F534}' :
                   indicator.severity === 'high' ? '\u{1F7E0}' :
                   indicator.severity === 'medium' ? '\u{1F7E1}' : '\u26AA'}
                </span>
                <span>{indicator.message}</span>
              </div>
            ))}
          </div>
        )}
        <div className="mt-3 p-2 bg-red-100 rounded text-sm">
          <strong>Peringatan:</strong> Penggunaan aplikasi GPS palsu dapat mengakibatkan sanksi administratif. Data GPS akan dianalisis lebih lanjut oleh sistem dan Admin.
        </div>
        {fakeGpsScore > 0 && (
          <div className="mt-2 text-xs text-red-600">Skor kecurigaan: {fakeGpsScore}/100</div>
        )}
      </AlertDescription>
    </Alert>
  );
};

export const TodayStatusAlert = ({ todayStatus }) => {
  if (!todayStatus) return null;
  const both = todayStatus.has_masuk && todayStatus.has_pulang;
  const masukOnly = todayStatus.has_masuk;

  return (
    <Alert className={`mb-4 ${
      both ? 'border-emerald-200 bg-emerald-50' : masukOnly ? 'border-blue-200 bg-blue-50' : 'border-gray-200 bg-gray-50'
    }`} data-testid="today-status-alert">
      <Info className={`w-5 h-5 ${both ? 'text-emerald-600' : masukOnly ? 'text-blue-600' : 'text-gray-600'}`} />
      <AlertDescription className={both ? 'text-emerald-800' : masukOnly ? 'text-blue-800' : 'text-gray-800'}>
        {todayStatus.message}
      </AlertDescription>
    </Alert>
  );
};

export const DuplicateWarnings = ({ todayStatus, absenType }) => {
  return (
    <>
      {todayStatus?.has_masuk && absenType === 'masuk' && (
        <Alert className="mb-4 border-amber-200 bg-amber-50" data-testid="duplicate-masuk-warning">
          <AlertCircle className="w-5 h-5 text-amber-600" />
          <AlertDescription className="text-amber-800">
            <strong>Anda sudah absen masuk hari ini!</strong><br />
            Jika ingin absen pulang, silakan klik tombol "Absen Pulang" di atas.
          </AlertDescription>
        </Alert>
      )}
      {todayStatus?.has_masuk && todayStatus?.has_pulang && (
        <Alert className="mb-4 border-emerald-200 bg-emerald-50" data-testid="complete-attendance-info">
          <Check className="w-5 h-5 text-emerald-600" />
          <AlertDescription className="text-emerald-800">
            <strong>Absensi hari ini sudah lengkap!</strong><br />
            Anda sudah melakukan absen masuk dan pulang hari ini.
          </AlertDescription>
        </Alert>
      )}
    </>
  );
};
